import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Tag,
  InputNumber,
  Radio,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  HolderOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dragula from "dragula";
import "dragula/dist/dragula.css";
import { useSelector, useDispatch } from "react-redux";
import {
  updateContactStatuses,
  updateLeadStatuses,
} from "../../../core/data/redux/slices/ProfileSlice";
import {
  setSelectedContactStatus as setContactSelectedStatus,
  clearContactsCache as clearContactsCache,
} from "../../../core/data/redux/slices/ContactSlice";
import {
  setSelectedContactStatus as setLeadSelectedStatus,
  clearContactsCache as clearLeadsCache,
} from "../../../core/data/redux/slices/LeadSlice";
import api from "../../../core/axios/axiosInstance";

const ContactStatuses = () => {
  // Refs for dragula containers
  const leadTableRef = useRef(null);
  // Contact Statuses State
  const [contactStatuses, setContactStatuses] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [editingContactStatus, setEditingContactStatus] = useState(null);
  const [contactForm] = Form.useForm();

  // Lead Statuses State
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadGroups, setLeadGroups] = useState([]);
  const [originalLeadGroupOrder, setOriginalLeadGroupOrder] = useState([]);
  const [leadOrderChanged, setLeadOrderChanged] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [isLeadModalVisible, setIsLeadModalVisible] = useState(false);
  const [editingLeadGroup, setEditingLeadGroup] = useState(null);
  const [leadForm] = Form.useForm();

  const userProfile = useSelector((state) => state.profile);
  const dispatch = useDispatch();

  // Helper function to group statuses by group number
  const groupStatuses = (statuses) => {
    const grouped = {};
    statuses.forEach((status) => {
      if (!grouped[status.group]) {
        grouped[status.group] = [];
      }
      grouped[status.group].push(status);
    });

    return Object.keys(grouped)
      .map((groupNum) => ({
        group: parseInt(groupNum),
        statuses: grouped[groupNum],
      }))
      .sort((a, b) => a.group - b.group);
  };

  // Load statuses from profile on mount
  useEffect(() => {
    if (
      userProfile?.contactStatuses &&
      userProfile.contactStatuses.length > 0
    ) {
      setContactStatuses(userProfile.contactStatuses);
    }
    if (userProfile?.leadStatuses && userProfile.leadStatuses.length > 0) {
      setLeadStatuses(userProfile.leadStatuses);
      const groups = groupStatuses(userProfile.leadStatuses);
      setLeadGroups(groups);
      setOriginalLeadGroupOrder(groups.map((g) => g.group));
      setLeadOrderChanged(false);
    }
  }, [userProfile]);

  // ============ DRAGULA SETUP ============
  useEffect(() => {
    let contactDrake = null;
    let leadDrake = null;

    // Setup dragula for lead table
    if (leadTableRef.current) {
      const tbody = leadTableRef.current.querySelector("tbody");
      if (tbody) {
        leadDrake = dragula([tbody], {
          moves: (el, container, handle) => {
            return handle.closest(".drag-handle") !== null;
          },
        });

        leadDrake.on("drop", (el, target, source, sibling) => {
          const rows = Array.from(tbody.querySelectorAll("tr"));
          const newOrder = rows
            .map((row) => {
              const groupNum = parseInt(row.getAttribute("data-row-key"));
              return leadGroups.find((g) => g.group === groupNum);
            })
            .filter(Boolean);

          setLeadGroups(newOrder);

          const newGroupOrder = newOrder.map((g) => g.group);
          const orderChanged =
            JSON.stringify(newGroupOrder) !==
            JSON.stringify(originalLeadGroupOrder);
          setLeadOrderChanged(orderChanged);
        });
      }
    }

    // Cleanup
    return () => {
      if (contactDrake) contactDrake.destroy();
      if (leadDrake) leadDrake.destroy();
    };
  }, [leadGroups, originalLeadGroupOrder]);

  const saveLeadGroupOrder = async () => {
    try {
      setLeadLoading(true);
      // Reassign group numbers based on new order
      const reorderedStatuses = [];
      leadGroups.forEach((groupData, index) => {
        const newGroupNumber = index + 1;
        groupData.statuses.forEach((status) => {
          reorderedStatuses.push({
            ...status,
            group: newGroupNumber,
          });
        });
      });

      await saveLeadStatuses(reorderedStatuses);
      message.success("Lead group order updated successfully");
      setOriginalLeadGroupOrder(leadGroups.map((g) => g.group));
      setLeadOrderChanged(false);
    } catch (error) {
      message.error("Failed to update lead group order");
    } finally {
      setLeadLoading(false);
    }
  };

  // ============ CONTACT STATUSES HANDLERS ============
  const handleAddContact = () => {
    setEditingContactStatus(null);
    contactForm.resetFields();
    setIsContactModalVisible(true);
  };

  const handleEditContact = (record) => {
    setEditingContactStatus(record);
    contactForm.setFieldsValue({
      label: record.label,
    });
    setIsContactModalVisible(true);
  };

  const handleDeleteContact = async (record) => {
    try {
      const updatedStatuses = contactStatuses.filter(
        (s) => s.value !== record.value
      );
      await saveContactStatuses(updatedStatuses);
      message.success("Contact status deleted successfully");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete contact status";
      message.error(errorMessage);
    }
  };

  const saveContactStatuses = async (updatedStatuses) => {
    setContactLoading(true);
    try {
      const response = await api.put("/editProfile/contact-statuses", {
        contactStatuses: updatedStatuses,
        category: "contact",
      });

      if (response.data.status === "success") {
        setContactStatuses(response.data.data.contactStatuses);
        dispatch(updateContactStatuses(response.data.data.contactStatuses));
        dispatch(setContactSelectedStatus([]));
        dispatch(clearContactsCache());
        return true;
      } else {
        throw new Error(
          response.data.message || "Failed to update contact statuses"
        );
      }
    } catch (error) {
      console.error("Error updating contact statuses:", error);
      // Re-throw with original error to preserve backend message
      throw error;
    } finally {
      setContactLoading(false);
    }
  };

  const handleSubmitContact = async () => {
    try {
      const values = await contactForm.validateFields();

      const generatedValue = values.label
        .trim()
        .toLowerCase()
        .replace(/\s+(.)/g, (match, char) => char.toUpperCase())
        .replace(/\s+/g, "");

      let updatedStatuses;
      if (editingContactStatus) {
        updatedStatuses = contactStatuses.map((s) =>
          s.value === editingContactStatus.value
            ? { value: generatedValue, label: values.label }
            : s
        );
      } else {
        const exists = contactStatuses.some((s) => s.value === generatedValue);
        if (exists) {
          message.error("A contact status with this label already exists");
          return;
        }
        updatedStatuses = [
          ...contactStatuses,
          { value: generatedValue, label: values.label },
        ];
      }

      await saveContactStatuses(updatedStatuses);
      message.success(
        `Contact status ${
          editingContactStatus ? "updated" : "added"
        } successfully`
      );
      setIsContactModalVisible(false);
      contactForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      const errorMessage =
        error.response?.data?.message ||
        `Failed to ${editingContactStatus ? "update" : "add"} contact status`;
      message.error(errorMessage);
    }
  };

  // ============ LEAD STATUSES HANDLERS ============
  const handleAddLead = () => {
    setEditingLeadGroup(null);
    const nextGroupNumber =
      leadGroups.length > 0
        ? Math.max(...leadGroups.map((g) => g.group)) + 1
        : 1;
    leadForm.resetFields();
    leadForm.setFieldsValue({
      group: nextGroupNumber,
      statuses: [{ label: "" }],
    });
    setIsLeadModalVisible(true);
  };

  const handleEditLead = (record) => {
    setEditingLeadGroup(record);
    const defaultStatus = record.statuses.find((s) => s.isDefault);
    leadForm.setFieldsValue({
      group: record.group,
      statuses: record.statuses.map((s) => ({ label: s.label })),
      defaultStatus: defaultStatus?.value || record.statuses[0]?.value || null,
    });
    setIsLeadModalVisible(true);
  };

  const handleDeleteLead = async (record) => {
    try {
      const updatedStatuses = leadStatuses.filter(
        (s) => s.group !== record.group
      );
      await saveLeadStatuses(updatedStatuses);
      message.success("Lead status group deleted successfully");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete lead status group";
      message.error(errorMessage);
    }
  };

  const saveLeadStatuses = async (updatedStatuses) => {
    setLeadLoading(true);
    try {
      const response = await api.put("/editProfile/contact-statuses", {
        contactStatuses: updatedStatuses,
        category: "lead",
      });

      if (response.data.status === "success") {
        setLeadStatuses(response.data.data.leadStatuses);
        setLeadGroups(groupStatuses(response.data.data.leadStatuses));
        dispatch(updateLeadStatuses(response.data.data.leadStatuses));
        dispatch(setLeadSelectedStatus([]));
        dispatch(clearLeadsCache());
        return true;
      } else {
        throw new Error(
          response.data.message || "Failed to update lead statuses"
        );
      }
    } catch (error) {
      console.error("Error updating lead statuses:", error);
      // Re-throw with original error to preserve backend message
      throw error;
    } finally {
      setLeadLoading(false);
    }
  };

  const handleSubmitLead = async () => {
    try {
      const values = await leadForm.validateFields();

      // Validate at least one status
      if (
        !values.statuses ||
        values.statuses.length === 0 ||
        !values.statuses[0].label
      ) {
        message.error("At least one status must be added to the group");
        return;
      }

      // If no default status selected, use first one
      const defaultStatusValue =
        values.defaultStatus ||
        values.statuses[0].label
          .trim()
          .toLowerCase()
          .replace(/\s+(.)/g, (match, char) => char.toUpperCase())
          .replace(/\s+/g, "");

      const newStatuses = values.statuses.map((s) => {
        const generatedValue = s.label
          .trim()
          .toLowerCase()
          .replace(/\s+(.)/g, (match, char) => char.toUpperCase())
          .replace(/\s+/g, "");

        return {
          value: generatedValue,
          label: s.label.trim(),
          group: values.group,
          isDefault: generatedValue === defaultStatusValue,
        };
      });

      let updatedStatuses;
      if (editingLeadGroup) {
        // Remove old statuses from this group and add new ones
        updatedStatuses = [
          ...leadStatuses.filter((s) => s.group !== editingLeadGroup.group),
          ...newStatuses,
        ];
      } else {
        // Check if group number already exists
        const groupExists = leadStatuses.some((s) => s.group === values.group);
        if (groupExists) {
          message.error("A group with this number already exists");
          return;
        }
        updatedStatuses = [...leadStatuses, ...newStatuses];
      }

      await saveLeadStatuses(updatedStatuses);
      message.success(
        `Lead status group ${
          editingLeadGroup ? "updated" : "added"
        } successfully`
      );
      setIsLeadModalVisible(false);
      leadForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      const errorMessage =
        error.response?.data?.message ||
        `Failed to ${editingLeadGroup ? "update" : "add"} lead status group`;
      message.error(errorMessage);
      console.error("Error submitting lead status group:", error);
    }
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      width: 200,
      sorter: (a, b) => a.label.localeCompare(b.label),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record, index, category) => {
        return (
          <Space size="middle">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() =>
                category === "contact"
                  ? handleEditContact(record)
                  : handleEditLead(record)
              }
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this status?"
              onConfirm={() =>
                category === "contact"
                  ? handleDeleteContact(record)
                  : handleDeleteLead(record)
              }
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Contact statuses columns
  const contactColumns = columns.map((col) => {
    if (col.key === "actions") {
      return {
        ...col,
        render: (_, record) => {
          if (record.value === "noAnswer")
            return (
              <Space size="middle">
                <Button type="disabled" danger>
                  Uneditable
                </Button>
              </Space>
            );
          return (
            <Space size="middle">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditContact(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Are you sure you want to delete this status?"
                onConfirm={() => handleDeleteContact(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      };
    }
    return col;
  });

  // Lead statuses columns
  const leadColumns = [
    {
      title: "",
      key: "drag",
      width: 50,
      render: () => (
        <span className="drag-handle" style={{ cursor: "move" }}>
          <HolderOutlined />
        </span>
      ),
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      sorter: (a, b) => a.group - b.group,
      width: 100,
    },
    {
      title: "Statuses",
      dataIndex: "statuses",
      key: "statuses",
      width: 300,
      render: (statuses) => (
        <Space wrap>
          {statuses.map((status, index) => (
            <span key={status.value}>
              {status.label}
              {index < statuses.length - 1 && " / "}
            </span>
          ))}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        if (
          record.statuses.some((s) => s.value === "interested") ||
          record.statuses.some((s) => s.value === "callBack") ||
          record.statuses.some((s) => s.value === "callSuccess")
        )
          return (
            <Space size="middle">
              <Button type="disabled" danger>
                Uneditable
              </Button>
            </Space>
          );

        return (
          <Space size="middle">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditLead(record)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this status group?"
              onConfirm={() => handleDeleteLead(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header d-flex mb-md-3 mb-0 mt-2 justify-content-between align-items-center">
          <div className="page-title mb-md-3 mb-0">
            <h4>Manage Contact & Lead Status Groups</h4>
            <h6>Customize your contact and lead status groups</h6>
          </div>
        </div>

        {/* Contact Statuses Section */}
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Contact Statuses</h5>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContact}
            >
              Add Contact Status
            </Button>
          </div>
          <div className="card-body">
            <Table
              columns={contactColumns}
              dataSource={contactStatuses}
              rowKey="value"
              loading={contactLoading}
              pagination={false}
            />
          </div>
        </div>

        {/* Lead Statuses Section */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Lead Status Groups</h5>
            <Space>
              {leadOrderChanged && (
                <Button
                  type="default"
                  icon={<SaveOutlined />}
                  onClick={saveLeadGroupOrder}
                  loading={leadLoading}
                >
                  Update Order
                </Button>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddLead}
              >
                Add Status Group
              </Button>
            </Space>
          </div>
          <div className="card-body" ref={leadTableRef}>
            <Table
              columns={leadColumns}
              dataSource={leadGroups}
              rowKey="group"
              loading={leadLoading}
              pagination={false}
            />
          </div>
        </div>

        {/* Contact Status Modal */}
        <Modal
          title={
            editingContactStatus ? "Edit Contact Status" : "Add Contact Status"
          }
          open={isContactModalVisible}
          onOk={handleSubmitContact}
          onCancel={() => {
            setIsContactModalVisible(false);
            contactForm.resetFields();
          }}
          confirmLoading={contactLoading}
          centered
        >
          <Form form={contactForm} layout="vertical">
            <Form.Item
              name="label"
              label="Status Label"
              rules={[{ required: true, message: "Please enter a label" }]}
            >
              <Input placeholder="e.g., Interested, Not Valid, Follow Up" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Lead Status Group Modal */}
        <Modal
          title={
            editingLeadGroup
              ? "Edit Lead Status Group"
              : "Add Lead Status Group"
          }
          open={isLeadModalVisible}
          onOk={handleSubmitLead}
          onCancel={() => {
            setIsLeadModalVisible(false);
            leadForm.resetFields();
          }}
          confirmLoading={leadLoading}
          centered
          width={600}
        >
          <Form form={leadForm} layout="vertical">
            <Form.Item name="group" label="Group Number">
              <InputNumber disabled style={{ width: "100%" }} />
            </Form.Item>
            <Form.List name="statuses">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Space
                      key={field.key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "label"]}
                        label={index === 0 ? "Status Labels" : ""}
                        rules={[
                          {
                            required: true,
                            message: "Please enter a status label",
                          },
                        ]}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <Input placeholder="e.g., Interested, Follow Up, Win" />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </Space>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Status
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.statuses !== currentValues.statuses
              }
            >
              {({ getFieldValue }) => {
                const statuses = getFieldValue("statuses") || [];
                if (statuses.length === 0 || !statuses[0]?.label) return null;

                const statusOptions = statuses
                  .filter((s) => s?.label)
                  .map((s, idx) => ({
                    value: s.label
                      .trim()
                      .toLowerCase()
                      .replace(/\s+(.)/g, (match, char) => char.toUpperCase())
                      .replace(/\s+/g, ""),
                    label: s.label,
                  }));

                return (
                  <Form.Item
                    name="defaultStatus"
                    label="Default Status for Group"
                  >
                    <Radio.Group>
                      {statusOptions.map((option, idx) => (
                        <Radio key={idx} value={option.value}>
                          {option.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ContactStatuses;
